import {
  calculateRoundedPercentage,
  roundToTwoDecimals,
} from 'common/helpers/';
import type { CallStats } from 'features/analytics/interfaces/';

export interface CallScoreMetrics {
  packetLossPercent: number;
  jitterMs: number;
  roundTripTimeMs: number;
  frameRateFps: number;
  qualityLimitationReason?: string;
  videoFreezeEvents?: number;
  totalVideoFreezeDurationMs?: number;
  avgVideoFreezeDurationMs?: number;
  frameDropRate?: number;
  jitterBufferDelayMs?: number;
  freezeRatePerMinute?: number;
  freezeTimePercentage?: number;
  estimatedCallDurationSeconds?: number;
}

export interface CallScoreResult {
  score: number;
  grade: string;
  metrics: CallScoreMetrics;
  breakdown: {
    packetLossScore: number;
    jitterScore: number;
    rttScore: number;
    frameRateScore: number;
    videoFreezeScore: number;
    frameDropScore: number;
  };
}

const SCORE_WEIGHTS = {
  videoFreezing: 0.3, // Highest - completely breaks communication experience
  packetLoss: 0.25, // Second highest - affects overall quality
  jitter: 0.2, // Third - very noticeable to users
  frameRate: 0.15, // Fourth - affects smoothness
  rtt: 0.05, // Lower - less noticeable in video calls
  frameDrops: 0.05, // Lowest - often correlates with other issues
};

interface CallMetricsInput {
  videoPacketsLost: number;
  audioPacketsLost: number;
  videoPacketsReceived: number;
  audioPacketsReceived: number;
  videoJitter: number;
  audioJitter: number;
  videoRtt: number;
  audioRtt: number;
  frameRate: number;
  qualityLimitationReason?: string;
  framesDecoded?: number;
  framesDropped?: number;
  framesReceived?: number;
  freezeCount?: number;
  totalFreezesDuration?: number;
  jitterBufferDelay?: number;
  jitterBufferEmittedCount?: number;
  estimatedCallDurationSeconds?: number;
}

/**
 * Calculates a comprehensive call quality score based on WebRTC statistics.
 *
 * This function analyzes various video call metrics including packet loss, jitter,
 * round-trip time, frame rate, and quality limitations to produce a composite
 * quality score from 0-100 with corresponding letter grades.
 *
 * The scoring algorithm uses weighted contributions from different metrics:
 * - Video Freezing: 30% weight (highest priority - breaks communication)
 * - Packet Loss: 25% weight
 * - Jitter: 20% weight
 * - Frame Rate: 15% weight
 * - Round Trip Time: 5% weight
 * - Frame Drops: 5% weight
 *
 * Quality limitation reasons (CPU, bandwidth, etc.) are tracked for diagnostics
 * but do not directly modify the score to avoid double penalizing.
 *
 * @param summary - The call statistics object containing inbound/outbound RTP stats
 *                  for both audio and video streams, including packet counts,
 *                  timing metrics, and quality limitation information
 *
 * @returns A CallScoreResult object containing:
 *          - score: Overall quality score (0-100, rounded to nearest integer)
 *          - grade: Letter grade (A/B/C/D/F or I for incomplete)
 *          - metrics: Raw metric values used for diagnostics
 *          - breakdown: Individual component scores before weighting
 *
 * @example
 * ```typescript
 * const callStats: CallStats = {
 *   inboundRtpVideo: { packetsLost: 5, packetsReceived: 1000, framesPerSecond: 30 },
 *   remoteInboundRtpVideo: { jitter: 0.01, roundTripTime: 0.05 },
 *   // ... other stats
 * };
 *
 * const result = calculateCallScore(callStats);
 * console.log(`Call quality: ${result.score} (${result.grade})`);
 * ```
 *
 * @remarks
 * - Returns score of 0 with grade 'I' (Incomplete) if required video stats are missing
 * - Requires valid frame rate > 0 to calculate meaningful scores
 * - Uses only video metrics for composite scoring but shows combined audio/video
 *   packet loss in diagnostic metrics
 * - Scores are clamped between 0-100 based on weighted metric contributions
 */
export const calculateCallScore = (summary: CallStats): CallScoreResult => {
  // Input validation
  if (!summary) {
    throw new Error('CallStats summary is required');
  }

  // Check for required video stats and valid framerate
  const videoStats = summary.inboundRtpVideo;
  const remoteVideoStats = summary.remoteInboundRtpVideo;

  const videoStatsComplete =
    hasRequiredVideoStats(summary) &&
    videoStats &&
    isStatNumber(videoStats.framesPerSecond) &&
    videoStats.framesPerSecond !== undefined &&
    videoStats.framesPerSecond > 0;

  if (!videoStatsComplete) {
    return {
      score: 0,
      grade: 'I', // Incomplete
      metrics: {
        packetLossPercent: 0,
        jitterMs: 0,
        roundTripTimeMs: 0,
        frameRateFps: 0,
        qualityLimitationReason:
          summary.outboundRtpVideo?.qualityLimitationReason,
        videoFreezeEvents: 0,
        totalVideoFreezeDurationMs: 0,
        avgVideoFreezeDurationMs: 0,
        frameDropRate: 0,
        jitterBufferDelayMs: 0,
      },
      breakdown: {
        packetLossScore: 0,
        jitterScore: 0,
        rttScore: 0,
        frameRateScore: 0,
        videoFreezeScore: 0,
        frameDropScore: 0,
      },
    };
  }

  const videoPacketsLost = extractStatNumber(videoStats.packetsLost);
  const videoPacketsReceived = extractStatNumber(videoStats.packetsReceived);
  const audioPacketsLost = extractStatNumber(
    summary.inboundRtpAudio?.packetsLost
  );
  const audioPacketsReceived = extractStatNumber(
    summary.inboundRtpAudio?.packetsReceived
  );

  const videoJitter = extractStatNumber(remoteVideoStats?.jitter);
  const audioJitter = extractStatNumber(summary.remoteInboundRtpAudio?.jitter);

  const videoRtt = extractStatNumber(remoteVideoStats?.roundTripTime);
  const audioRtt = extractStatNumber(
    summary.remoteInboundRtpAudio?.roundTripTime
  );

  const frameRate = extractStatNumber(videoStats.framesPerSecond);
  const qualityLimitationReason =
    summary.outboundRtpVideo?.qualityLimitationReason;

  const framesDecoded = extractStatNumber(videoStats.framesDecoded);
  const framesDropped = extractStatNumber(videoStats.framesDropped);
  const framesReceived = extractStatNumber(videoStats.framesReceived);
  const freezeCount = extractStatNumber(videoStats.freezeCount);
  const totalFreezesDuration = extractStatNumber(
    videoStats.totalFreezesDuration
  );
  const jitterBufferDelay = extractStatNumber(videoStats.jitterBufferDelay);
  const jitterBufferEmittedCount = extractStatNumber(
    videoStats.jitterBufferEmittedCount
  );

  // Use only VIDEO scores for composite scoring
  const packetLossScore = calculatePacketLossScore(
    videoPacketsLost,
    videoPacketsReceived
  );
  const jitterScore = calculateJitterScore(videoJitter);
  const rttScore = calculateRttScore(videoRtt);

  const frameRateScore = calculateFrameRateScore(frameRate);

  // Estimate call duration for freeze scoring normalization
  // Use frames decoded or received as proxy for call duration
  const estimatedCallDurationSeconds = Math.max(
    framesDecoded && frameRate > 0 ? framesDecoded / frameRate : 0,
    framesReceived && frameRate > 0 ? framesReceived / frameRate : 0
  );

  const videoFreezeScore = calculateVideoFreezeScore(
    freezeCount,
    totalFreezesDuration,
    estimatedCallDurationSeconds
  );
  const frameDropScore = calculateFrameDropScore(framesReceived, framesDropped);

  const baseScore =
    packetLossScore * SCORE_WEIGHTS.packetLoss +
    jitterScore * SCORE_WEIGHTS.jitter +
    rttScore * SCORE_WEIGHTS.rtt +
    frameRateScore * SCORE_WEIGHTS.frameRate +
    videoFreezeScore * SCORE_WEIGHTS.videoFreezing +
    frameDropScore * SCORE_WEIGHTS.frameDrops;

  const finalScore = Math.max(0, Math.min(100, baseScore));

  const metrics = calculateCallMetrics({
    videoPacketsLost,
    audioPacketsLost,
    videoPacketsReceived,
    audioPacketsReceived,
    videoJitter,
    audioJitter,
    videoRtt,
    audioRtt,
    frameRate,
    qualityLimitationReason,
    framesDecoded,
    framesDropped,
    framesReceived,
    freezeCount,
    totalFreezesDuration,
    jitterBufferDelay,
    jitterBufferEmittedCount,
    estimatedCallDurationSeconds,
  });

  return {
    score: Math.round(finalScore),
    grade: getGrade(finalScore),
    metrics,
    breakdown: {
      packetLossScore: Math.round(packetLossScore),
      jitterScore: Math.round(jitterScore),
      rttScore: Math.round(rttScore),
      frameRateScore: Math.round(frameRateScore),
      videoFreezeScore: Math.round(videoFreezeScore),
      frameDropScore: Math.round(frameDropScore),
    },
  };
};

const isStatNumber = (value: any): boolean =>
  typeof value === 'number' && !Number.isNaN(value);

const hasRequiredVideoStats = (summary: CallStats): boolean => {
  const videoStats = summary.inboundRtpVideo;
  const remoteVideoStats = summary.remoteInboundRtpVideo;

  if (!videoStats || !remoteVideoStats) {
    return false;
  }

  return (
    isStatNumber(videoStats.packetsLost) &&
    isStatNumber(videoStats.packetsReceived) &&
    isStatNumber(videoStats.framesPerSecond) &&
    isStatNumber(remoteVideoStats.jitter) &&
    isStatNumber(remoteVideoStats.roundTripTime)
  );
};

const extractStatNumber = (value: number | number[] | undefined): number => {
  if (Array.isArray(value)) {
    const lastValue = value.length > 0 ? value[value.length - 1] : 0;
    return Number.isFinite(lastValue) ? lastValue : 0;
  }
  const numValue = value ?? 0;
  return Number.isFinite(numValue) ? numValue : 0;
};

const calculatePacketLossScore = (
  packetsLost: number,
  packetsReceived: number
): number => {
  // Ensure non-negative inputs
  const safePacketsLost = Math.max(0, packetsLost);
  const safePacketsReceived = Math.max(0, packetsReceived);

  const totalPackets = safePacketsLost + safePacketsReceived;
  if (totalPackets === 0) {
    return 100;
  }
  const packetLossRate = (safePacketsLost / totalPackets) * 100;

  if (packetLossRate === 0) {
    return 100;
  }
  // Each band linearly interpolates from its ceiling score down to its floor score.
  // The multiplier is the slope: how many score points to subtract per 1% of packet loss.
  // Derived as: (ceiling_score - floor_score) / band_width_percent
  //
  if (packetLossRate <= 0.03) {
    return Math.max(93, 100 - packetLossRate * 233);
  }
  if (packetLossRate <= 0.1) {
    return Math.max(78, 93 - (packetLossRate - 0.03) * 214);
  }
  if (packetLossRate <= 0.5) {
    return Math.max(60, 78 - (packetLossRate - 0.1) * 45);
  }
  if (packetLossRate <= 1) {
    return Math.max(35, 60 - (packetLossRate - 0.5) * 50);
  }
  if (packetLossRate <= 2) {
    return Math.max(0, 35 - (packetLossRate - 1) * 35);
  }

  return 0;
};

const calculateJitterScore = (jitter: number): number => {
  // Convert jitter from seconds to milliseconds, ensure non-negative
  const jitterMs = Math.max(0, jitter) * 1000;

  if (jitterMs <= 5) {
    return 100;
  }
  if (jitterMs <= 15) {
    return Math.max(80, 100 - (jitterMs - 5) * 2);
  }
  if (jitterMs <= 30) {
    return Math.max(60, 80 - (jitterMs - 15) * 1.33);
  }
  if (jitterMs <= 60) {
    return Math.max(35, 60 - (jitterMs - 30) * 0.83);
  }

  return Math.max(0, 35 - (jitterMs - 60) * 0.29);
};

const calculateRttScore = (rtt: number): number => {
  const rttMs = Math.max(0, rtt) * 1000;

  if (rttMs <= 50) {
    return 100;
  }
  if (rttMs <= 100) {
    return Math.max(85, 100 - (rttMs - 50) * 0.3);
  }
  if (rttMs <= 200) {
    return Math.max(70, 85 - (rttMs - 100) * 0.15);
  }
  if (rttMs <= 400) {
    return Math.max(50, 70 - (rttMs - 200) * 0.1);
  }

  return Math.max(0, 50 - (rttMs - 400) * 0.05);
};

const calculateFrameRateScore = (fps: number): number => {
  const safeFps = Math.max(0, fps);

  // Modern video call expectations: 30fps is ideal, 24+ is good
  if (safeFps >= 30) {
    return 100; // Perfect - smooth video
  }
  if (safeFps >= 24) {
    return 90; // Good - acceptable for video calls
  }
  if (safeFps >= 20) {
    return 80; // Okay - slight stuttering may be noticeable
  }
  if (safeFps >= 15) {
    return 60; // Poor - noticeable stuttering
  }
  if (safeFps >= 10) {
    return 30; // Very poor - significant stuttering
  }

  return Math.max(0, safeFps * 2); // Harsh penalty for very low FPS
};

/**
 * Calculates penalty score based on freeze rate per minute using interpreting-grade QoE bands.
 *
 * QoE (Quality of Experience) is a user-centered measure of perceived call quality,
 * especially important for live two-way video interpreting. It reflects how well
 * users can communicate, sign, and lip-read without disruption. QoE bands are based
 * on user studies and industry standards for what is considered "excellent," "good,"
 * "fair," "poor," or "unacceptable" in real-time video calls.
 *
 * These bands are tuned for sign language interpreting, where even minor freezes
 * can disrupt comprehension and turn-taking. The penalty function assigns higher
 * penalties as freeze rates increase, matching what users actually feel in live sessions.
 *
 * Bands:
 *   - Excellent: 0–0.05 freezes/min (≤0.3s, ≤0.3% stalled) → 0–5 points
 *   - Good: 0.05–0.15 freezes/min (≤0.7s, 0.3–0.7%) → 5–15 points
 *   - Fair: 0.15–0.5 freezes/min (0.7–1.5s, 0.7–2%) → 15–40 points
 *   - Poor: 0.5–1.0 freezes/min (1.5–3s, 2–3.5%) → 40–70 points
 *   - Unacceptable: >1 freeze/min or any single freeze >2.5s (>3s, >3.5–5%+) → 70–100 points
 *
 * @param freezeRatePerMinute - Number of freezes per minute
 * @returns Penalty score (0-100 points)
 */
const calculateFreezePenaltyByRate = (freezeRatePerMinute: number): number => {
  // Interpreting-grade QoE bands for live two-way video
  // See above for band details

  if (freezeRatePerMinute <= 0.05) {
    // Excellent
    return freezeRatePerMinute * 100; // 0–5 points
  } else if (freezeRatePerMinute <= 0.15) {
    // Good
    return 5 + (freezeRatePerMinute - 0.05) * (10 / 0.1); // 5–15 points
  } else if (freezeRatePerMinute <= 0.5) {
    // Fair
    return 15 + (freezeRatePerMinute - 0.15) * (25 / 0.35); // 15–40 points
  } else if (freezeRatePerMinute <= 1) {
    // Poor
    return 40 + (freezeRatePerMinute - 0.5) * (30 / 0.5); // 40–70 points
  } else {
    // Unacceptable
    return Math.min(100, 70 + (freezeRatePerMinute - 1) * 30); // 70–100 points
  }
};

const calculateVideoFreezeScore = (
  freezeCount: number,
  totalFreezesDuration: number,
  callDurationSeconds?: number
): number => {
  const safeCount = Math.max(0, Math.floor(freezeCount));
  const safeDuration = Math.max(0, totalFreezesDuration);

  if (safeCount === 0) {
    return 100;
  }

  // Convert duration from seconds to milliseconds for calculation
  const freezeDurationMs = safeDuration * 1000;

  // If we have call duration, normalize penalties by call length
  if (callDurationSeconds && callDurationSeconds > 0) {
    const callDurationMinutes = callDurationSeconds / 60;

    // Calculate freeze rate per minute
    const freezeRatePerMinute = safeCount / callDurationMinutes;

    // Calculate freeze time as percentage of total call time
    const freezeTimePercentage = (safeDuration / callDurationSeconds) * 100;

    // Score based on freeze frequency per minute (normalized)
    const frequencyPenalty = calculateFreezePenaltyByRate(freezeRatePerMinute);

    // Score based on freeze time percentage (normalized)
    /*
      The multipliers (200, 133, 167) are chosen so that the penalty reaches the intended upper bound for each band:
        - 200: At 0.05%, penalty is 10 points (0.05 * 200 = 10)
        - 133: At 0.2%, penalty is 30 points (10 + (0.2 - 0.05) * 133 = 30)
        - 167: At 0.5%, penalty is 80 points (30 + (0.5 - 0.2) * 167 = 80)
      This ensures a smooth, predictable penalty curve for freeze time percentage.
    */
    let durationPenalty = 0;
    if (freezeTimePercentage <= 0.05) {
      // <= 0.05% of call time frozen - minor issue
      durationPenalty = freezeTimePercentage * 200; // Up to 10 points
    } else if (freezeTimePercentage <= 0.2) {
      // <= 0.2% of call time frozen - noticeable
      durationPenalty = 10 + (freezeTimePercentage - 0.05) * 133; // Up to 30 points
    } else if (freezeTimePercentage <= 0.5) {
      // <= 0.5% of call time frozen - concerning
      durationPenalty = 30 + (freezeTimePercentage - 0.2) * 167; // Up to 80 points
    } else {
      // > 0.5% of call time frozen - very poor
      durationPenalty = 80 + Math.min(20, (freezeTimePercentage - 0.5) * 40); // Max 100
    }

    // Take the worse of the two penalties (not additive to avoid double penalty)
    const totalPenalty = Math.max(frequencyPenalty, durationPenalty);
    return Math.max(0, 100 - totalPenalty);
  }

  // Fallback to original absolute scoring when call duration is unknown
  // Score based on frequency of freezes
  let frequencyPenalty = 0;
  if (safeCount === 1) {
    frequencyPenalty = 10; // 1 freeze is noticeable but not terrible
  } else if (safeCount === 2) {
    frequencyPenalty = 15; // 2 freezes still acceptable if short
  } else if (safeCount <= 5) {
    frequencyPenalty = 15 + (safeCount - 2) * 15; // Escalating penalty
  } else {
    frequencyPenalty = 60 + Math.min(40, (safeCount - 5) * 8); // Max 100
  }

  // Score based on total duration of freezes - consider context
  let durationPenalty = 0;
  if (freezeDurationMs <= 500) {
    // <= 0.5 seconds total - minor issue
    durationPenalty = freezeDurationMs / 50; // Up to 10 points
  } else if (freezeDurationMs <= 1500) {
    // <= 1.5 seconds - noticeable but manageable
    durationPenalty = 10 + (freezeDurationMs - 500) / 50; // Up to 30 points
  } else if (freezeDurationMs <= 3000) {
    // <= 3 seconds - getting bad
    durationPenalty = 30 + (freezeDurationMs - 1500) / 30; // Up to 80 points
  } else {
    durationPenalty = 80 + Math.min(20, (freezeDurationMs - 3000) / 100); // Max 100
  }

  // Take the worse of the two penalties (not additive to avoid double penalty)
  const totalPenalty = Math.max(frequencyPenalty, durationPenalty);

  return Math.max(0, 100 - totalPenalty);
};

const calculateFrameDropScore = (
  framesReceived: number,
  framesDropped: number
): number => {
  const safeReceived = Math.max(0, Math.floor(framesReceived));
  const safeDropped = Math.max(0, Math.floor(framesDropped));

  if (safeReceived === 0) {
    return 100;
  }

  const totalFrames = safeReceived + safeDropped;
  if (totalFrames === 0) {
    return 100;
  }

  const dropRate = (safeDropped / totalFrames) * 100;

  if (dropRate === 0) {
    return 100;
  }
  // Reasonable penalties for frame drops - they affect smoothness
  if (dropRate <= 0.1) {
    return Math.max(90, 100 - dropRate * 100); // Minor penalty for small drops
  }
  if (dropRate <= 0.5) {
    return Math.max(75, 90 - (dropRate - 0.1) * 37.5);
  }
  if (dropRate <= 1) {
    return Math.max(60, 75 - (dropRate - 0.5) * 30);
  }
  if (dropRate <= 2) {
    return Math.max(40, 60 - (dropRate - 1) * 20);
  }

  return Math.max(0, 40 - (dropRate - 2) * 20);
};

const getGrade = (score: number): string => {
  if (score >= 90) {
    return 'A';
  }
  if (score >= 80) {
    return 'B';
  }
  if (score >= 70) {
    return 'C';
  }
  if (score >= 60) {
    return 'D';
  }
  return 'F';
};

interface FreezeRateMetrics {
  freezeRatePerMinute: number;
  freezeTimePercentage: number;
}

const calculateFreezeRateMetrics = (
  freezeCount: number,
  totalFreezesDuration: number,
  framesDecoded: number,
  framesReceived: number,
  frameRate: number
): FreezeRateMetrics => {
  if (frameRate <= 0 || (framesDecoded <= 0 && framesReceived <= 0)) {
    return { freezeRatePerMinute: 0, freezeTimePercentage: 0 };
  }

  const callDurationSeconds = Math.max(
    framesDecoded > 0 ? framesDecoded / frameRate : 0,
    framesReceived > 0 ? framesReceived / frameRate : 0
  );
  const callDurationMinutes = callDurationSeconds / 60;

  const freezeRatePerMinute =
    callDurationMinutes > 0
      ? roundToTwoDecimals(freezeCount / callDurationMinutes)
      : 0;
  const freezeTimePercentage =
    callDurationSeconds > 0
      ? roundToTwoDecimals((totalFreezesDuration / callDurationSeconds) * 100)
      : 0;

  return { freezeRatePerMinute, freezeTimePercentage };
};

const calculateCallMetrics = (input: CallMetricsInput): CallScoreMetrics => {
  const {
    videoPacketsLost,
    audioPacketsLost,
    videoPacketsReceived,
    audioPacketsReceived,
    videoJitter,
    audioJitter,
    videoRtt,
    audioRtt,
    frameRate,
    qualityLimitationReason,
    framesDecoded = 0,
    framesDropped = 0,
    framesReceived = 0,
    freezeCount = 0,
    totalFreezesDuration = 0,
    jitterBufferDelay = 0,
    jitterBufferEmittedCount = 0,
    estimatedCallDurationSeconds = 0,
  } = input;

  // Calculate packet loss percentage from combined audio/video streams
  const totalPacketsLost = videoPacketsLost + audioPacketsLost;
  const totalPacketsReceived = videoPacketsReceived + audioPacketsReceived;
  const totalPacketsTransmitted = totalPacketsLost + totalPacketsReceived;

  const packetLossPercent =
    totalPacketsTransmitted > 0
      ? calculateRoundedPercentage(totalPacketsLost, totalPacketsTransmitted)
      : 0;

  // Calculate average jitter from video and audio streams, convert seconds to milliseconds
  const averageJitterSeconds = (videoJitter + audioJitter) / 2;
  const averageJitterMilliseconds = averageJitterSeconds * 1000;
  const jitterMs = roundToTwoDecimals(averageJitterMilliseconds);

  // Calculate average round trip time from video and audio streams, convert seconds to milliseconds
  const averageRttSeconds = (videoRtt + audioRtt) / 2;
  const averageRttMilliseconds = averageRttSeconds * 1000;
  const roundTripTimeMs = roundToTwoDecimals(averageRttMilliseconds);

  // Round frame rate to two decimal places for consistent display
  const frameRateFps = roundToTwoDecimals(frameRate);

  // Calculate frame drop rate
  const totalFrames = framesReceived + framesDropped;
  const frameDropRate =
    totalFrames > 0
      ? roundToTwoDecimals((framesDropped / totalFrames) * 100)
      : 0;

  // Convert duration from seconds to milliseconds
  const totalVideoFreezeDurationMs = roundToTwoDecimals(
    totalFreezesDuration * 1000
  );
  const avgVideoFreezeDurationMs =
    freezeCount > 0
      ? roundToTwoDecimals((totalFreezesDuration / freezeCount) * 1000)
      : 0;

  // Calculate freeze rate per minute and freeze time percentage
  const { freezeRatePerMinute, freezeTimePercentage } =
    calculateFreezeRateMetrics(
      freezeCount,
      totalFreezesDuration,
      framesDecoded,
      framesReceived,
      frameRate
    );

  // Calculate AVERAGE jitter buffer delay per packet (not cumulative total)
  const jitterBufferDelayMs =
    jitterBufferEmittedCount > 0
      ? roundToTwoDecimals(
          (jitterBufferDelay / jitterBufferEmittedCount) * 1000
        )
      : 0;

  return {
    packetLossPercent,
    jitterMs,
    roundTripTimeMs,
    frameRateFps,
    qualityLimitationReason,
    videoFreezeEvents: freezeCount,
    totalVideoFreezeDurationMs,
    avgVideoFreezeDurationMs,
    frameDropRate,
    jitterBufferDelayMs,
    freezeRatePerMinute,
    freezeTimePercentage,
    estimatedCallDurationSeconds: roundToTwoDecimals(
      estimatedCallDurationSeconds
    ),
  };
};
